import React from 'react'
import type { SelectionBoxProps } from './SelectionBox.types'
import { useSelectionBoxGroupContext } from './SelectionBoxContext'
import './SelectionBox.css'

const SelectionBox: React.FC<SelectionBoxProps> = ({
  title,
  description,
  showDescription,
  showSelectIndicator = true,
  selected,
  disabled = false,
  value,
  darkMode = false,
  className = '',
  onSelect,
  previewState,
}) => {
  const group = useSelectionBoxGroupContext()
  const isPreview = previewState != null
  const isDark = darkMode || Boolean(group?.darkMode)
  const isDisabled = disabled || Boolean(group?.disabled)
  const isSelected =
    previewState === 'selected' ||
    (group && value != null ? group.selectedValue === value : Boolean(selected))

  const shouldShowDescription =
    showDescription ?? (description != null && description !== '')
  const isInteractive =
    !isPreview &&
    !isDisabled &&
    (onSelect != null || (group != null && value != null))

  const classNames = [
    'ds-selection-box',
    isSelected ? 'ds-selection-box--selected' : '',
    isDisabled ? 'ds-selection-box--disabled' : '',
    isDark ? 'ds-selection-box--dark' : '',
    isInteractive ? 'ds-selection-box--interactive' : '',
    isPreview ? 'ds-selection-box--preview' : '',
    previewState ? `ds-selection-box--preview-${previewState}` : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  const handleSelect = (): void => {
    if (!isInteractive) {
      return
    }

    if (group && value != null) {
      group.onValueChange(value)
    }

    onSelect?.()
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (!isInteractive) {
      return
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      handleSelect()
    }
  }

  return (
    <div
      className={classNames}
      role="radio"
      aria-checked={isSelected}
      aria-disabled={isDisabled || undefined}
      tabIndex={isInteractive ? 0 : isPreview ? -1 : undefined}
      data-preview-state={previewState}
      onClick={handleSelect}
      onKeyDown={handleKeyDown}
    >
      <div className="ds-selection-box__content">
        <p className="ds-selection-box__title">{title}</p>
        {shouldShowDescription && description ? (
          <p className="ds-selection-box__description">{description}</p>
        ) : null}
      </div>

      {isSelected && showSelectIndicator ? (
        <p className="ds-selection-box__indicator">Selected</p>
      ) : null}
    </div>
  )
}

export default SelectionBox
